/*
 * frost-lifestyle.js (owner: CONTENT) – <fb-parallax data-enabled>
 * Gentle scroll parallax for [data-fb-parallax="<speed>"] children (speed ≈ fraction of the
 * distance between the viewport centre and the item's reference box, clamped to ±48 px, ±16 px
 * below 750 px where the tiles sit closer). Reference = nearest [data-fb-parallax-group] (shared,
 * so items with equal speed move in lockstep and never collide), else the item's parent. Only in motion "full",
 * only while the section is near the viewport (IntersectionObserver), one rAF per scroll frame,
 * transform only. Listens to frost:motion-change and resets cleanly on unload.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('fb-parallax')) return;

  var MAX = 48;
  var MAX_MOBILE = 16;

  function motion() {
    if (window.FROSTBEULE && typeof window.FROSTBEULE.motion === 'function') return window.FROSTBEULE.motion();
    return document.documentElement.getAttribute('data-motion') || 'full';
  }

  window.customElements.define(
    'fb-parallax',
    class extends HTMLElement {
      connectedCallback() {
        this.items = Array.prototype.slice.call(this.querySelectorAll('[data-fb-parallax]'));
        if (!this.hasAttribute('data-enabled') || !this.items.length) return;
        this.refs = this.items.map(function (el) {
          return (el.parentElement && el.parentElement.closest('[data-fb-parallax-group]')) || el.parentElement;
        });

        this._onScroll = this.onScroll.bind(this);
        this._onMotion = this.onMotion.bind(this);
        this.active = false;
        this.ticking = false;

        var self = this;
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                self.setActive(entry.isIntersecting);
              });
            },
            { rootMargin: '20% 0px 20% 0px', threshold: 0 }
          );
          this.io.observe(this);
        }
        document.addEventListener('frost:motion-change', this._onMotion);
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
        this.io = null;
        this.setActive(false);
        if (this._onMotion) document.removeEventListener('frost:motion-change', this._onMotion);
        this.reset();
      }

      setActive(on) {
        on = !!on && motion() === 'full';
        if (on === this.active) return;
        this.active = on;
        if (on) {
          window.addEventListener('scroll', this._onScroll, { passive: true });
          window.addEventListener('resize', this._onScroll, { passive: true });
          this.onScroll();
        } else if (this._onScroll) {
          window.removeEventListener('scroll', this._onScroll);
          window.removeEventListener('resize', this._onScroll);
        }
      }

      onMotion() {
        if (motion() !== 'full') {
          this.setActive(false);
          this.reset();
        } else if (this.io) {
          /* re-evaluate visibility */
          this.io.unobserve(this);
          this.io.observe(this);
        }
      }

      reset() {
        (this.items || []).forEach(function (el) {
          el.style.transform = '';
        });
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        var self = this;
        window.requestAnimationFrame(function () {
          self.ticking = false;
          if (!self.active) return;
          self.update();
        });
      }

      update() {
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var vw = window.innerWidth || document.documentElement.clientWidth;
        var centre = vh / 2;
        var max = vw < 750 ? MAX_MOBILE : MAX;
        var refs = this.refs;
        /* read all, then write all (no layout thrash) */
        var values = this.items.map(function (el, i) {
          var rect = refs[i].getBoundingClientRect();
          var speed = parseFloat(el.getAttribute('data-fb-parallax')) || 0;
          var offset = (rect.top + rect.height / 2 - centre) * speed;
          return Math.max(-max, Math.min(max, offset));
        });
        this.items.forEach(function (el, i) {
          el.style.transform = 'translate3d(0,' + values[i].toFixed(1) + 'px,0)';
        });
      }
    }
  );
})();
